import { AutoComplete, Input } from 'antd';
import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import _ from 'lodash';
import styled from 'styled-components';

import servicesRequest, { SEARCH_URL } from '../../Config/services.config';
import {
	FlexDisplayMediumGap,
	FlexDisplaySpaceBetween,
} from '../../Screens/Preferences/Preferences.styled';
import { ColoredLabel } from '../../Screens/Industries/Industries.desktop.screen';
import { getDefaultMarket } from '../../Utils';

interface IAutoComplete {
	onClickConfirm: (value: any[]) => (value1: string) => void;
	additionalAction?: () => void;
	searchScope?: 'all' | 'company' | 'industry';
	placeholder?: string;
}

const EMPTY_OPTIONS = {
	label: 'Not found',
};

const SearchInput = styled(Input.Search)`
	.ant-input {
		padding-right: 8px;
		text-overflow: ellipsis;
	}

	.ant-input::placeholder {
		color: #7a7a7a;
	}

	.ant-input:focus::placeholder {
		color: transparent;
	}
`;

const AutoCompleteComponent = (props: IAutoComplete) => {
	const [searchValue, setSearchValue] = useState('');
	const [options, setOptions] = useState<any[]>([]);
	const [isComposing, setIsComposing] = useState(false);

	const {
		onClickConfirm,
		additionalAction = () => {},
		searchScope = 'all',
		placeholder = 'Search companies or industries',
	} = props;

	const request = useMemo(() => servicesRequest(), []);

	const renderLabel = (code: string, name: string, index: number) => (
		<FlexDisplaySpaceBetween className="my-1">
			<FlexDisplayMediumGap>
				<ColoredLabel
					key={index}
					sector={code}
					className="text-white standard-code fw-600 fs-10">
					{code}
				</ColoredLabel>
				<span className="ms-1 cursor-pointer fw-500 fs-12">{name}</span>
			</FlexDisplayMediumGap>
		</FlexDisplaySpaceBetween>
	);

	useEffect(() => {
		if (isComposing) {
			return undefined;
		}

		const query = searchValue.trim();

		if (query.length < 2) {
			setOptions([]);
			return undefined;
		}

		const controller = new AbortController();
		const debounceTimer = window.setTimeout(() => {
			request
				.get(SEARCH_URL, {
					params: {
						country_id: getDefaultMarket(),
						q: query,
					},
					signal: controller.signal,
				})
				.then((response: any) => {
					const companiesResponse = _.get(response, 'data.data.companies', []);
					const industriesResponse = _.get(
						response,
						'data.data.industries',
						[]
					);
					const mappedCompanies = companiesResponse.map(
						(d: any, index: number) => {
							return {
								label: renderLabel(d.stock_code, d.name, index),
								value: d.name,
								key: d.stock_code,
								id: d.id,
								isCompany: true,
							};
						}
					);
					const mappedIndustries = industriesResponse.map(
						(d: any, index: number) => {
							return {
								label: renderLabel(d.industry_code, d.name, index),
								value: d.name,
								key: d.industry_code,
								id: d.id,
								isCompany: false,
							};
						}
					);
					/* // eslint-disable-next-line @typescript-eslint/ban-ts-comment
								// @ts-ignore */
					let mappedData = [...mappedIndustries, ...mappedCompanies];

					if (searchScope === 'company') {
						mappedData = mappedCompanies;
					}

					if (searchScope === 'industry') {
						mappedData = mappedIndustries;
					}

					if (mappedData.length === 0) {
						mappedData = [EMPTY_OPTIONS];
					}
					/* // eslint-disable-next-line @typescript-eslint/ban-ts-comment
								// @ts-ignore */
					setOptions(mappedData);
				})
				.catch(error => {
					if (!axios.isCancel(error)) {
						toast.error('Something went wrong. Please refresh the page.');
					}
				});
		}, 600);

		return () => {
			window.clearTimeout(debounceTimer);
			controller.abort();
		};
	}, [isComposing, request, searchScope, searchValue]);

	return (
		<AutoComplete
			options={options}
			onSelect={(value, option) => {
				onClickConfirm([option.value, option.key, option.isCompany])(option.id);
				setSearchValue('');
				additionalAction();
			}}
			style={{ width: '100%', height: 'inherit' }}
			onSearch={text => {
				setSearchValue(text);
			}}
			value={searchValue}>
			<SearchInput
				size="small"
				placeholder={placeholder}
				onCompositionStart={() => setIsComposing(true)}
				onCompositionEnd={event => {
					setSearchValue(event.currentTarget.value);
					setIsComposing(false);
				}}
			/>
		</AutoComplete>
	);
};

export default AutoCompleteComponent;
